import React, { useState, useEffect } from 'react';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Switch } from '../../../components/ui/switch';
import { toast } from 'sonner';
import { Gift, Plane, Power, RefreshCw, Users } from 'lucide-react';

const money = (v) => `RM ${Number(v || 0).toLocaleString('en-MY', { maximumFractionDigits: 0 })}`;
const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700', paused: 'bg-amber-100 text-amber-700',
  closed: 'bg-gray-100 text-gray-600', finalised: 'bg-indigo-100 text-indigo-700',
};

const AdminIncentives = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [lp, setLp] = useState([]);
  const [tr, setTr] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [c, l, t] = await Promise.all([
        api.get('/admin/incentives/campaigns'),
        api.get('/admin/incentives/leader-pool'),
        api.get('/admin/incentives/travel'),
      ]);
      setCampaigns(c.data.campaigns || []);
      setLp((l.data.cohort || []).filter((e) => e.net_units > 0));
      setTr((t.data.cohort || []).filter((e) => e.total_gdv > 0));
    } catch (e) { toast.error('Failed to load incentives'); }
    finally { setLoading(false); }
  };

  const toggle = async (camp, on) => {
    try {
      await api.post(`/admin/incentives/campaigns/${camp.code}/toggle`, { status: on ? 'active' : 'paused' });
      toast.success(`${camp.code} ${on ? 'activated' : 'paused'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Toggle failed'); }
  };

  const runBackfill = async () => {
    try {
      const res = await api.post('/admin/incentives/backfill?apply=true');
      toast.success(`Backfill: ${res.data.emitted} bookings replayed`);
      load();
    } catch (e) { toast.error('Backfill failed'); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-incentives-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-pink-600 flex items-center justify-center text-white"><Gift className="w-6 h-6" /></div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">2026 Incentive Programmes</h1>
          <p className="text-sm text-gray-500">Leader Pool &amp; Performance Travel · 1 Jul – 31 Dec 2026</p>
        </div>
        <Button variant="outline" className="ml-auto" onClick={runBackfill} data-testid="incentive-backfill-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Replay SPA-signed
        </Button>
      </div>

      {/* Campaign toggles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {campaigns.map((c) => (
          <Card key={c.code} data-testid={`campaign-${c.code}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.programme === 'leader_pool' ? 'bg-purple-100 text-purple-600' : 'bg-sky-100 text-sky-600'}`}>
                {c.programme === 'leader_pool' ? <Users className="w-5 h-5" /> : <Plane className="w-5 h-5" />}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{c.programme === 'leader_pool' ? 'Leader Pool' : 'Performance Travel'}</p>
                <p className="text-xs text-gray-500">{c.code} · v{c.config_version}</p>
              </div>
              <Badge className={STATUS_COLORS[c.status] || 'bg-gray-100'}>{c.status}</Badge>
              {['active', 'paused'].includes(c.status) && (
                <div className="flex items-center gap-2">
                  <Power className="w-4 h-4 text-gray-400" />
                  <Switch checked={c.status === 'active'} onCheckedChange={(v) => toggle(c, v)} data-testid={`toggle-${c.code}`} />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="animate-spin w-7 h-7 border-2 border-pink-600 border-t-transparent rounded-full" /></div>
      ) : (
        <Tabs defaultValue="lp">
          <TabsList>
            <TabsTrigger value="lp" data-testid="admin-tab-lp">Leader Pool ({lp.length})</TabsTrigger>
            <TabsTrigger value="tr" data-testid="admin-tab-travel">Travel ({tr.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="lp" className="mt-4">
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr>
                  <th className="text-left p-3">Leader</th><th className="text-right p-3">Net Units</th>
                  <th className="text-right p-3">Blocks</th><th className="text-right p-3">Residual</th>
                  <th className="text-right p-3">Reward</th></tr></thead>
                <tbody>
                  {lp.length === 0 ? (<tr><td colSpan={5} className="text-center text-gray-400 py-8">No leader pool credits yet</td></tr>) :
                    lp.map((e) => (
                      <tr key={e.agent_id} className="border-t">
                        <td className="p-3">{e.agent?.agent_code || '—'} · {e.agent?.full_name || e.agent_id.slice(0, 8)}</td>
                        <td className="p-3 text-right font-medium">{e.net_units}</td>
                        <td className="p-3 text-right">{e.completed_blocks}</td>
                        <td className="p-3 text-right">{e.residual_units}</td>
                        <td className="p-3 text-right font-semibold text-emerald-600">{money(e.provisional_reward_rm)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>
          <TabsContent value="tr" className="mt-4">
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr>
                  <th className="text-left p-3">Agent</th><th className="text-right p-3">Total GDV</th>
                  <th className="text-right p-3">Full-Time GDV</th><th className="text-right p-3">Tier</th></tr></thead>
                <tbody>
                  {tr.length === 0 ? (<tr><td colSpan={4} className="text-center text-gray-400 py-8">No travel GDV yet</td></tr>) :
                    tr.map((e) => (
                      <tr key={e.agent_id} className="border-t">
                        <td className="p-3">{e.agent?.agent_code || '—'} · {e.agent?.full_name || e.agent_id.slice(0, 8)}</td>
                        <td className="p-3 text-right">{money(e.total_gdv)}</td>
                        <td className="p-3 text-right">{money(e.full_time_gdv)}</td>
                        <td className="p-3 text-right">{e.provisional_tier ? <Badge className="bg-sky-100 text-sky-700">{e.provisional_tier} · {e.provisional_country}</Badge> : <span className="text-gray-400">—</span>}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default AdminIncentives;
